import React from 'react';

export interface AttestationBoxProps {
  /** Full legal attestation text to display */
  text: string;
  /** Whether the user has checked the acknowledgment (starts false) */
  checked: boolean;
  /** Called when checkbox is toggled */
  onCheckedChange: (checked: boolean) => void;
  /** Optional label override (default: "I have read and agree to the above.") */
  label?: string;
  /** Optional id for the checkbox (accessibility) */
  id?: string;
  /** Optional class for the container */
  className?: string;
  /** If true, submit/primary action is disabled until checked */
  required?: boolean;
  /** If true, highlights the checkbox to draw attention (e.g., when user tries to submit without checking) */
  highlight?: boolean;
}

/**
 * Reusable attestation block: shows legal text and a checkbox that must be
 * manually checked. Checkbox starts unchecked; use onCheckedChange to enable submit.
 */
const AttestationBox: React.FC<AttestationBoxProps> = ({
  text,
  checked,
  onCheckedChange,
  label = 'I have read and agree to the above.',
  id = 'attestation-checkbox',
  className = '',
  required = true,
  highlight = false,
}) => {
  return (
    <div
      className={`rounded-lg border ${highlight ? 'border-red-500 ring-2 ring-red-200' : 'border-gray-200'} bg-gray-50 p-4 transition-all duration-300 ${className}`}
      role="region"
      aria-labelledby={id ? `${id}-heading` : undefined}
    >
      {id && (
        <h3 id={`${id}-heading`} className="sr-only">
          Attestation
        </h3>
      )}
      <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-line mb-4">
        {text}
      </p>
      <label className="flex items-start gap-3 cursor-pointer group">
        <input
          type="checkbox"
          id={id}
          checked={checked}
          onChange={(e) => onCheckedChange(e.target.checked)}
          className={`mt-1 w-5 h-5 rounded ${highlight ? 'border-red-500 ring-2 ring-red-300' : 'border-gray-300'} text-purple-600 focus:ring-purple-500 cursor-pointer transition-all duration-300`}
          aria-required={required}
          aria-describedby={id ? `${id}-label` : undefined}
        />
        <span
          id={id ? `${id}-label` : undefined}
          className={`text-sm font-medium ${highlight ? 'text-red-600' : 'text-gray-900'} group-hover:text-gray-700 transition-colors duration-300`}
        >
          {required && <span className="text-red-500 mr-1">*</span>}
          {label}
        </span>
      </label>
    </div>
  );
};

export default AttestationBox;
